import type {
  AccountCreateInput,
  AccountReconcileResponse,
  AccountResponse,
  CategoryResponse,
  DashboardResponse,
  RecurringItemCreateInput,
  RecurringItemResponse,
  SavingsGoalCreateInput,
  SavingsGoalResponse,
  SavingsGoalUpdateInput,
  SavingsOverviewResponse,
  SettingsPayload,
  TransactionCreateInput,
  TransactionListResponse,
  TransactionResponse,
  TransactionSplitInput,
  TransactionUpdateInput,
} from '@budget/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export const keys = {
  dashboard: (accountId?: string) => ['dashboard', accountId ?? 'default'] as const,
  accounts: ['accounts'] as const,
  categories: ['categories'] as const,
  transactions: (accountId?: string) => ['transactions', accountId ?? 'all'] as const,
};

export function useDashboard(accountId?: string) {
  return useQuery({
    queryKey: keys.dashboard(accountId),
    queryFn: () =>
      api<DashboardResponse>(
        accountId === undefined ? '/dashboard' : `/dashboard?accountId=${accountId}`,
      ),
  });
}

export function useAccounts() {
  return useQuery({
    queryKey: keys.accounts,
    queryFn: () => api<AccountResponse[]>('/accounts'),
  });
}

export function useCategories() {
  return useQuery({
    queryKey: keys.categories,
    queryFn: () => api<CategoryResponse[]>('/categories'),
    // Les catégories bougent rarement : inutile de les redemander sans cesse.
    staleTime: 5 * 60 * 1000,
  });
}

export function useTransactions(accountId?: string) {
  return useQuery({
    queryKey: keys.transactions(accountId),
    queryFn: () =>
      api<TransactionListResponse>(
        accountId === undefined ? '/transactions?limit=100' : `/transactions?limit=100&accountId=${accountId}`,
      ),
  });
}

export function useCreateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: TransactionCreateInput) =>
      api<TransactionResponse>('/transactions', { method: 'POST', body: input }),
    // Une saisie change le solde, la courbe et la liste : on invalide large
    // plutôt que de recalculer à la main ce que le serveur sait déjà.
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useSplitTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: TransactionSplitInput) =>
      api<TransactionListResponse>('/transactions/split', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string } & TransactionUpdateInput) => {
      const { id, ...changes } = input;
      return api<TransactionResponse>(`/transactions/${id}`, { method: 'PATCH', body: changes });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useDeleteTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api<void>(`/transactions/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useReconcileAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { accountId: string; balanceCents: number }) =>
      api<AccountReconcileResponse>(`/accounts/${input.accountId}/reconcile`, {
        method: 'POST',
        body: { balanceCents: input.balanceCents },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AccountCreateInput) =>
      api<AccountResponse>('/accounts', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useRecurringItems() {
  return useQuery({
    queryKey: ['recurring'],
    queryFn: () => api<RecurringItemResponse[]>('/recurring-items'),
  });
}

export function useCreateRecurringItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: RecurringItemCreateInput) =>
      api<RecurringItemResponse>('/recurring-items', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useDeleteRecurringItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api<void>(`/recurring-items/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useSavings() {
  return useQuery({
    queryKey: ['savings'],
    queryFn: () => api<SavingsOverviewResponse>('/savings'),
  });
}

export function useCreateSavingsGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SavingsGoalCreateInput) =>
      api<SavingsGoalResponse>('/savings', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useUpdateSavingsGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { goalId: string } & SavingsGoalUpdateInput) => {
      const { goalId, ...changes } = input;
      return api<SavingsGoalResponse>(`/savings/${goalId}`, { method: 'PATCH', body: changes });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useSavingsTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { goalId: string; amountCents: number; fromAccountId?: string }) =>
      api<SavingsGoalResponse>(`/savings/${input.goalId}/transfer`, {
        method: 'POST',
        body: {
          amountCents: input.amountCents,
          ...(input.fromAccountId !== undefined && { fromAccountId: input.fromAccountId }),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useWithdrawFromSavings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { goalId: string; toAccountId: string; amountCents: number }) =>
      api<SavingsGoalResponse>(`/savings/${input.goalId}/withdraw`, {
        method: 'POST',
        body: { toAccountId: input.toAccountId, amountCents: input.amountCents },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useArchiveSavingsGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goalId: string) =>
      api<SavingsGoalResponse>(`/savings/${goalId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => api<SettingsPayload>('/settings'),
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Partial<SettingsPayload>) =>
      api<SettingsPayload>('/settings', { method: 'PATCH', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}
